import Quill from 'quill'
import { Delta, Op } from 'quill/core'
import { forwardRef, MutableRefObject, useEffect, useRef } from 'react'

type EditorProps = {
  defaultValue: string
  setContent: (value: string) => void
}

export const Editor = forwardRef<Quill, EditorProps>(
  ({ defaultValue, setContent }, ref) => {
    const containerRef = useRef(null)
    const initializedRef = useRef(false)

    const inputRef = ref as MutableRefObject<Quill | null>

    useEffect(() => {
      if (
        containerRef &&
        containerRef.current &&
        inputRef &&
        !initializedRef.current
      ) {
        const container: HTMLDivElement = containerRef.current

        const editorContainer: HTMLDivElement = container.appendChild(
          container.ownerDocument.createElement('div'),
        )

        const toolbarOptions = [
          [{ header: [2, 3, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          ['clean'],
        ]

        const quill = new Quill(editorContainer, {
          theme: 'snow',
          modules: {
            toolbar: toolbarOptions,
          },
        })

        quill.on('text-change', () => {
          setContent(JSON.stringify(quill.getContents()))
        })

        inputRef.current = quill

        // Set initial content if provided
        if (defaultValue) {
          try {
            const contents = JSON.parse(defaultValue)
            const contentsOps = contents.ops as Op[] | { ops: Op[] } | undefined
            quill.setContents(new Delta(contentsOps))
          } catch (error) {
            // If defaultValue is not JSON, treat as plain text
            quill.setText(defaultValue)
          }
        }

        initializedRef.current = true

        return () => {
          inputRef.current = null
          container.innerHTML = ''
          initializedRef.current = false
        }
      }
    }, [inputRef])

    return <div ref={containerRef}></div>
  },
)

Editor.displayName = 'Editor'
